/**
 * React Query 캐시 무효화 유틸리티
 *
 * 프로젝트 전반에서 사용되는 캐시 무효화 패턴을 통합합니다.
 * 중복 코드를 줄이고 일관된 캐시 관리를 보장합니다.
 */

import { QueryClient } from '@tanstack/react-query';
import {
  getPlaylistListQueryKey,
  getPlaylistRetrieveQueryKey,
  getPlaylistGetTracksQueryKey,
  getPlaylistGetStatusQueryKey,
  getTrackListQueryKey,
  getTrackRetrieveQueryKey,
  getTrackGetVersionsQueryKey,
  getUserMeQueryKey,
  getMusicVideoListQueryKey,
  getMusicVideoRetrieveQueryKey,
  getAudioArrangementListQueryKey,
  getAudioArrangementRetrieveQueryKey,
} from '@/lib/api/orval';

// ============================================================================
// 플레이리스트 캐시 무효화
// ============================================================================

/**
 * 플레이리스트 목록 캐시 무효화
 * 플레이리스트 생성/삭제 후 목록 갱신에 사용
 */
export function invalidatePlaylistList(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: getPlaylistListQueryKey() });
}

/**
 * 특정 플레이리스트 캐시 무효화
 * 플레이리스트 수정/액션 후 상세 정보 갱신에 사용
 */
export function invalidatePlaylist(queryClient: QueryClient, playlistId: string): void {
  queryClient.invalidateQueries({ queryKey: getPlaylistRetrieveQueryKey(playlistId) });
}

/**
 * 플레이리스트 트랙 목록 캐시 무효화
 * 메타데이터 생성/트랙 추가 후 트랙 목록 갱신에 사용
 */
export function invalidatePlaylistTracks(queryClient: QueryClient, playlistId: string): void {
  queryClient.invalidateQueries({ queryKey: getPlaylistGetTracksQueryKey(playlistId) });
}

/**
 * 플레이리스트 상태 캐시 무효화
 * 상태 변경 후 상태 정보 갱신에 사용
 */
export function invalidatePlaylistStatus(queryClient: QueryClient, playlistId: string): void {
  queryClient.invalidateQueries({ queryKey: getPlaylistGetStatusQueryKey(playlistId) });
}

/**
 * 플레이리스트와 트랙 모두 무효화
 * 메타데이터 생성/트랙 추가 등 트랙 변경이 있는 액션 후 사용
 */
export function invalidatePlaylistWithTracks(queryClient: QueryClient, playlistId: string): void {
  invalidatePlaylist(queryClient, playlistId);
  invalidatePlaylistTracks(queryClient, playlistId);
  invalidatePlaylistStatus(queryClient, playlistId);
}

// ============================================================================
// 트랙 캐시 무효화
// ============================================================================

/**
 * 트랙 목록 캐시 무효화
 * 트랙 생성/삭제 후 목록 갱신에 사용
 */
export function invalidateTrackList(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: getTrackListQueryKey() });
}

/**
 * 특정 트랙 캐시 무효화
 * 트랙 수정/액션 후 상세 정보 갱신에 사용
 */
export function invalidateTrack(queryClient: QueryClient, trackId: string): void {
  queryClient.invalidateQueries({ queryKey: getTrackRetrieveQueryKey(trackId) });
}

/**
 * 트랙 버전 목록 캐시 무효화
 * 버전 선택/토글 후 버전 목록 갱신에 사용
 */
export function invalidateTrackVersions(queryClient: QueryClient, trackId: string): void {
  queryClient.invalidateQueries({ queryKey: getTrackGetVersionsQueryKey(trackId) });
}

/**
 * 트랙과 버전 캐시 일괄 무효화
 * 트랙 상세 + 버전 목록 모두 무효화
 */
export function invalidateTrackAndVersions(queryClient: QueryClient, trackId: string): void {
  invalidateTrack(queryClient, trackId);
  invalidateTrackVersions(queryClient, trackId);
}

// ============================================================================
// 트랙 + 플레이리스트 복합 무효화
// ============================================================================

/**
 * 트랙 액션 후 캐시 무효화
 * 트랙 수정/삭제 시 트랙 + 플레이리스트 + 플레이리스트 트랙 목록 캐시 모두 무효화
 */
export function invalidateTrackWithPlaylist(
  queryClient: QueryClient,
  playlistId: string,
  trackId?: string
): void {
  if (trackId) {
    invalidateTrack(queryClient, trackId);
  }
  invalidateTrackList(queryClient);
  invalidatePlaylist(queryClient, playlistId);
  // 플레이리스트 트랙 목록도 무효화 (트랙 수정 시 UI 즉시 반영을 위해)
  invalidatePlaylistTracks(queryClient, playlistId);
}

/**
 * 트랙 버전 액션 후 캐시 무효화
 * 버전 선택/토글/확장 시 트랙 + 버전 + 플레이리스트 + 트랙목록 캐시 무효화
 */
export function invalidateTrackVersionWithPlaylist(
  queryClient: QueryClient,
  trackId: string,
  playlistId?: string
): void {
  invalidateTrackAndVersions(queryClient, trackId);
  if (playlistId) {
    invalidatePlaylist(queryClient, playlistId);
    // 플레이리스트 트랙 목록도 무효화 (버전 정보가 트랙에 포함되어 있음)
    invalidatePlaylistTracks(queryClient, playlistId);
  }
}

// ============================================================================
// 뮤직비디오 캐시 무효화
// ============================================================================

/**
 * 뮤직비디오 목록 캐시 무효화
 */
export function invalidateMusicVideoList(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: getMusicVideoListQueryKey() });
}

/**
 * 특정 뮤직비디오 캐시 무효화
 */
export function invalidateMusicVideo(queryClient: QueryClient, musicVideoId: string): void {
  queryClient.invalidateQueries({ queryKey: getMusicVideoRetrieveQueryKey(musicVideoId) });
}

/**
 * 뮤직비디오 목록 + 상세 모두 무효화
 */
export function invalidateMusicVideoAll(queryClient: QueryClient, musicVideoId: string): void {
  invalidateMusicVideoList(queryClient);
  invalidateMusicVideo(queryClient, musicVideoId);
}

// ============================================================================
// 어레인지먼트 캐시 무효화
// ============================================================================

/**
 * 어레인지먼트 목록 캐시 무효화
 */
export function invalidateArrangementList(queryClient: QueryClient, playlistId?: string): void {
  queryClient.invalidateQueries({
    queryKey: getAudioArrangementListQueryKey(playlistId ? { playlist: playlistId } : undefined),
  });
}

/**
 * 특정 어레인지먼트 캐시 무효화
 */
export function invalidateArrangement(queryClient: QueryClient, arrangementId: string): void {
  queryClient.invalidateQueries({ queryKey: getAudioArrangementRetrieveQueryKey(arrangementId) });
}

/**
 * 어레인지먼트 목록 + 상세 모두 무효화
 */
export function invalidateArrangementAll(queryClient: QueryClient, arrangementId: string, playlistId?: string): void {
  invalidateArrangementList(queryClient, playlistId);
  invalidateArrangement(queryClient, arrangementId);
}

// ============================================================================
// 사용자 캐시 무효화
// ============================================================================

/**
 * 현재 사용자 정보 캐시 무효화
 * 로그인/로그아웃 후 사용자 정보 갱신에 사용
 */
export function invalidateUserMe(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: getUserMeQueryKey() });
}
